import React, { useState, useRef, useContext, useEffect } from "react";
import PropTypes from "prop-types";
import cx from "classnames";

import {
  ReactDefaultPropsWithChildren,
  ReactComponentStaticProps,
} from "../../types/react";
import { useUniqueId } from "../hooks/useUniqueId";
import LpgCollapseOutline from "../../icons/Lpg/CollapseOutline";
import { ThemeContext } from "../Theme";
import { Icon } from "../Icon";

import { DropdownInputContext } from "./Context";

import Messages from "./Messages";

import styles from "./DropdownInput.scss";

export const DropdownInputTagName = "div";
export type DropdownInputDefaultTag = typeof DropdownInputTagName;

export const DropdownInputContentTagName = "div";
export type DropdownInputContentDefaultTag = typeof DropdownInputContentTagName;

const dropdownInputVariants = ["box", "naked", "inline"] as const;
export type DropdownInputVariantValue = typeof dropdownInputVariants[number];

export type DropdownInputBaseProps<
  Tag extends React.ElementType = DropdownInputDefaultTag,
  ContentTag extends React.ElementType = DropdownInputContentDefaultTag
> = {
  tag?: Tag;
  contentTag?: ContentTag;
  variants?: DropdownInputVariantValue[];
  id?: string;
  customClass?: string;
  contentCustomClass?: string;
  label: React.ReactNode;
  value?: string;
  isValid?: boolean;
  required?: boolean;
  disabled?: boolean;
  hintMessage?: string;
  invalidMessage?: string;
  staticContent?: React.ReactNode;
  onBlur?: (
    value: string | undefined,
    open: boolean,
    event: React.FocusEvent<HTMLElement>
  ) => void;
  onChange?: (value: string | undefined, open: boolean) => void;
  afterOpen?: (value: string | undefined) => void;
  afterClose?: (value: string | undefined) => void;
  isOpen?: boolean;
  buttonProps?: React.ComponentPropsWithoutRef<"button">;
};

export type DropdownInputProps<
  Tag extends React.ElementType = DropdownInputDefaultTag,
  ContentTag extends React.ElementType = DropdownInputContentDefaultTag
> = DropdownInputBaseProps<Tag, ContentTag> &
  Omit<
    React.ComponentPropsWithRef<Tag>,
    keyof DropdownInputBaseProps<Tag, ContentTag>
  >;

const defaultProps = {
  tag: DropdownInputTagName,
  contentTag: DropdownInputContentTagName,
  variants: ["box"] as DropdownInputVariantValue[],
  id: undefined,
  customClass: undefined,
  contentCustomClass: undefined,
  value: undefined,
  isValid: true,
  required: false,
  disabled: false,
  hintMessage: "",
  invalidMessage: "",
  staticContent: undefined,
  onChange: undefined,
  onBlur: undefined,
  afterOpen: undefined,
  afterClose: undefined,
  isOpen: false,
  buttonProps: {},
  children: null,
} as const;

const validatedDefaultProps: ReactDefaultPropsWithChildren<
  DropdownInputBaseProps,
  typeof defaultProps
> = defaultProps;

export type DropdownInputPropsWithoutRef<
  Tag extends React.ElementType = DropdownInputDefaultTag,
  ContentTag extends React.ElementType = DropdownInputContentDefaultTag
> = DropdownInputBaseProps<Tag, ContentTag> &
  Omit<
    React.ComponentPropsWithoutRef<Tag>,
    keyof DropdownInputBaseProps<Tag, ContentTag>
  >;

const DropdownInputWithRef = <
  Tag extends React.ElementType = DropdownInputDefaultTag,
  ContentTag extends React.ElementType = DropdownInputContentDefaultTag
>(
  props: DropdownInputPropsWithoutRef<Tag, ContentTag>,
  ref: React.ComponentPropsWithRef<Tag>["ref"]
) => {
  const {
    tag: CustomTag = defaultProps.tag,
    contentTag: CustomContentTag = defaultProps.contentTag,
    variants = defaultProps.variants,
    id = defaultProps.id,
    customClass = defaultProps.customClass,
    contentCustomClass = defaultProps.contentCustomClass,
    label,
    value: controlledValue = defaultProps.value,
    isValid,
    required,
    disabled,
    hintMessage = defaultProps.hintMessage,
    invalidMessage = defaultProps.invalidMessage,
    staticContent = defaultProps.staticContent,
    onBlur: onInputBlur,
    onChange,
    afterOpen,
    afterClose,
    isOpen: controlledIsOpen = defaultProps.isOpen,
    buttonProps,
    children,
    ...wrapperProps
  } = props;

  const mainRef = useRef<HTMLElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const setElementRef = (
    elementRef: React.MutableRefObject<HTMLElement | null>,
    forwardedRef:
      | React.MutableRefObject<HTMLElement | null>
      | React.RefCallback<HTMLElement>
  ) => (element: HTMLElement | null) => {
    // eslint-disable-next-line no-param-reassign
    elementRef.current = element;
    if (typeof forwardedRef === "function") {
      forwardedRef(element);
    } else if (forwardedRef) {
      // eslint-disable-next-line no-param-reassign
      forwardedRef.current = element;
    }
  };

  const dropdownInputId =
    id || `dropdown-control-${useUniqueId("DropdownInput", props) + 1}`;

  const theme = useContext(ThemeContext);

  const [lastControlledIsOpen, setLastControlledIsOpen] = useState(
    controlledIsOpen
  );
  const [isOpen, setIsOpen] = useState(controlledIsOpen);
  if (controlledIsOpen !== lastControlledIsOpen) {
    setLastControlledIsOpen(controlledIsOpen);
    setIsOpen(controlledIsOpen);
  }

  const [lastControlledValue, setLastControlledValue] = useState(
    controlledValue
  );
  const [value, setValue] = useState(controlledValue);
  if (controlledValue !== lastControlledValue) {
    setLastControlledValue(controlledValue);
    setValue(controlledValue);
  }

  const [visitedOnce, setVisitedOnce] = useState(false);

  const focusButton = () => {
    buttonRef.current?.focus();
  };

  const close = () => {
    setIsOpen(false);
    requestAnimationFrame(() => {
      focusButton();
      if (onChange) onChange(value, false);
      if (afterClose) afterClose(value);
    });
  };

  const moveFocus = (by = 1) => {
    if (!contentRef.current) return;
    const { activeElement } = document;
    if (!activeElement) {
      focusButton();
      return;
    }
    const allFocusable = Array.from(
      contentRef.current.querySelectorAll(`.${styles.focus}`)
    );
    const currentIndex = allFocusable.indexOf(activeElement);
    let newIndex;
    if (currentIndex < 0) {
      newIndex = by < 0 ? allFocusable.length - 1 : 0;
    } else {
      newIndex = currentIndex + by;
      if (newIndex < 0) newIndex = allFocusable.length - 1;
      if (newIndex >= allFocusable.length) newIndex = 0;
    }
    const toFocus = allFocusable[newIndex] as HTMLElement;
    if (!toFocus) return;
    toFocus.focus();
  };

  const focusFirstItem = () => {
    if (!contentRef.current) return;
    const firstItem =
      contentRef.current.querySelector(`.${styles.focus}`) ||
      contentRef.current.querySelector(`.${styles.item}`);
    if (!firstItem) {
      contentRef.current.focus();
      return;
    }
    (firstItem as HTMLElement).focus();
  };

  const open = () => {
    setIsOpen(true);
    requestAnimationFrame(() => {
      focusFirstItem();
      if (onChange) onChange(value, true);
      if (afterOpen) afterOpen(value);
    });
  };

  const toggle = (event?: React.MouseEvent<HTMLButtonElement>) => {
    if (event) {
      if (event.nativeEvent.clientX === 0) return;
      event.preventDefault();
    }
    if (isOpen) close();
    else open();
  };

  const onBlur: React.FocusEventHandler<HTMLElement> = (event) => {
    const hoverElements = document.querySelectorAll(":hover");
    const hoverElement =
      hoverElements.item(hoverElements.length - 1) || event.relatedTarget;
    if (
      !visitedOnce &&
      (!hoverElement || !mainRef.current?.contains(hoverElement as Node))
    )
      setVisitedOnce(true);
    if (onInputBlur) onInputBlur(value, isOpen, event);
  };

  const onContentBlur = (event: FocusEvent) => {
    event.stopImmediatePropagation();
    const hoverElements = document.querySelectorAll(":hover");
    const hoverElement =
      hoverElements.item(hoverElements.length - 1) || event.relatedTarget;
    if (!hoverElement) {
      close();
      return;
    }
    if (hoverElement === buttonRef.current) return;
    if (buttonRef.current?.contains(hoverElement as Node)) return;
    if (contentRef?.current?.contains(hoverElement as Node)) return;
    close();
  };

  const onKeyDown: React.KeyboardEventHandler<HTMLElement> = (event) => {
    switch (event.key) {
      case " ":
        if (!isOpen) {
          event.preventDefault();
          event.stopPropagation();
          open();
        }
        break;
      case "Enter":
        event.preventDefault();
        event.stopPropagation();
        toggle();
        break;
      case "ArrowDown":
        event.preventDefault();
        event.stopPropagation();
        if (!isOpen) {
          open();
          return;
        }
        moveFocus(1);
        break;
      case "ArrowUp":
        event.preventDefault();
        event.stopPropagation();
        if (!isOpen) {
          open();
          requestAnimationFrame(() => {
            moveFocus(-1);
          });
          return;
        }
        moveFocus(-1);
        break;
      case "Escape":
        event.preventDefault();
        event.stopPropagation();
        close();
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (contentRef.current)
      contentRef.current.addEventListener("blur", onContentBlur, true);
    return () => {
      if (contentRef.current)
        contentRef.current.addEventListener("blur", onContentBlur, true);
    };
  }, [contentRef.current]);

  const shouldDisplayInvalidState = () =>
    !isValid || (required && visitedOnce && !value);

  const labelId = `${dropdownInputId}-label`;

  const themeClass = (theme && styles[theme]) || "";

  return (
    <CustomTag
      ref={setElementRef(mainRef, ref)}
      className={cx(
        styles.wrapper,
        {
          [themeClass]: theme,
          ...dropdownInputVariants.reduce(
            (classMap, variant) => ({
              ...classMap,
              [styles[variant]]:
                variants.findIndex((val) => val === variant) >= 0,
            }),
            {}
          ),
          "is-disabled": disabled,
        },
        customClass
      )}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      {...wrapperProps}
    >
      <label aria-label={labelId} htmlFor={dropdownInputId}>
        <button
          type="button"
          ref={buttonRef}
          className={cx(styles.button)}
          onClick={toggle}
          id={dropdownInputId}
          disabled={disabled}
          aria-labelledby={`${labelId}`}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          {...buttonProps}
        >
          <span
            className={cx(styles.input, {
              "is-active": !!value,
              "is-invalid": shouldDisplayInvalidState(),
            })}
          >
            <span className={styles.iconSpace} />
            {value || "\u00A0"}
          </span>
          <span className={cx(styles.labelWrapper)}>
            <span className={styles.label}>
              {label}
              {required ? "*" : null}
            </span>
            <span className={styles.icon}>
              <Icon icon={LpgCollapseOutline} size={Icon.Size.xsmall} />
            </span>
          </span>
        </button>
      </label>

      <DropdownInputContext.Provider
        value={{
          open,
          close,
          toggle,
          isOpen,
          setValue,
          itemClass: styles.item,
          focusClass: styles.focus,
        }}
      >
        <CustomContentTag
          ref={contentRef}
          tabIndex={-1}
          className={cx(
            styles.content,
            {
              [styles.contentOpen]: isOpen,
            },
            contentCustomClass
          )}
          role="listbox"
          aria-labelledby={`${labelId}`}
        >
          {isOpen && children}
        </CustomContentTag>

        {staticContent}
      </DropdownInputContext.Provider>

      <Messages
        hintMessage={hintMessage}
        invalidMessage={invalidMessage}
        isValid={!shouldDisplayInvalidState()}
      />
    </CustomTag>
  );
};

export const DropdownInput = (React.forwardRef(
  DropdownInputWithRef
) as unknown) as (<Tag extends React.ElementType = DropdownInputDefaultTag>(
  props: DropdownInputProps<Tag>
) => ReturnType<typeof DropdownInputWithRef>) &
  ReactComponentStaticProps<DropdownInputProps, "tag" | "contentTag"> & {
    Context: typeof DropdownInputContext;
    Messages: typeof Messages;
  };

DropdownInput.displayName = "DropdownInput";

DropdownInput.propTypes = {
  tag: PropTypes.elementType,
  contentTag: PropTypes.elementType,
  variants: PropTypes.arrayOf(
    PropTypes.oneOf(dropdownInputVariants).isRequired
  ),
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  id: PropTypes.string,
  customClass: PropTypes.string,
  contentCustomClass: PropTypes.string,
  label: PropTypes.string.isRequired,
  value: PropTypes.any,
  isValid: PropTypes.bool,
  disabled: PropTypes.bool,
  invalidMessage: PropTypes.string,
  hintMessage: PropTypes.string,
  required: PropTypes.bool,
  staticContent: PropTypes.element,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  afterOpen: PropTypes.func,
  afterClose: PropTypes.func,
  isOpen: PropTypes.bool,
  buttonProps: PropTypes.objectOf(PropTypes.any),
} as const;

DropdownInput.defaultProps = validatedDefaultProps;

DropdownInput.Context = DropdownInputContext;
DropdownInput.Messages = Messages;

export default DropdownInput;
